import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Partner,
  getPartnerById,
  isValidPartnerId,
  DEFAULT_PARTNER,
} from "../config/partners";

/**
 * Hook to detect and manage partner co-branding from URL parameters.
 *
 * URL Pattern: /?partner_id=socs&school_id=...&email=...
 *
 * @example
 * const { partner, isPartnerBranded, partnerId } = usePartner();
 *
 * if (isPartnerBranded) {
 *   // Show partner logo and custom messaging
 * }
 */
export const usePartner = () => {
  const [searchParams] = useSearchParams();

  // Extract partner_id from URL
  const partnerId = searchParams.get("partner_id");

  // Get partner configuration
  const partner: Partner = useMemo(() => {
    return getPartnerById(partnerId);
  }, [partnerId]);

  // Check if this is a valid partner (not default Pedagogue)
  const isPartnerBranded = useMemo(() => {
    return isValidPartnerId(partnerId);
  }, [partnerId]);

  // Extract additional URL parameters that might be passed by partners
  const schoolId = searchParams.get("school_id");
  const email = searchParams.get("email");
  const referralCode = searchParams.get("ref");

  return {
    /**
     * The partner configuration object
     */
    partner,

    /**
     * Whether the current page has valid partner branding
     */
    isPartnerBranded,

    /**
     * The raw partner_id from URL (may be null)
     */
    partnerId,

    /**
     * School ID if provided by partner
     */
    schoolId,

    /**
     * Pre-filled email if provided by partner
     */
    email,

    /**
     * Referral code if provided
     */
    referralCode,

    /**
     * Default Pedagogue partner config (for comparison)
     */
    defaultPartner: DEFAULT_PARTNER,
  };
};

export default usePartner;
